import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authClient } from "@/lib/auth-client";
import { api } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { inputClass } from "@/components/ui";

/** Écran de connexion : e-mail + mot de passe, ou « Se connecter avec Apple ». */
export function LoginPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const next = params.get("next") || "/";
  const [mode, setMode] = useState<"signin" | "signup">(params.get("mode") === "signup" ? "signup" : "signin");
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [apple, setApple] = useState(false);

  useEffect(() => {
    api.get<{ apple: boolean }>("/auth-config").then((c) => setApple(c.apple)).catch(() => {});
  }, []);

  const done = async () => {
    await queryClient.invalidateQueries();
    navigate(next, { replace: true });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const res =
      mode === "signin"
        ? await authClient.signIn.email({ email, password })
        : await authClient.signUp.email({ email, password, name: name || email.split("@")[0] });
    setBusy(false);
    if (res.error) {
      setError(
        res.error.status === 401
          ? "E-mail ou mot de passe incorrect."
          : res.error.message ?? "Connexion impossible."
      );
      return;
    }
    await done();
  };

  const signInApple = async () => {
    setError(null);
    await authClient.signIn.social({ provider: "apple", callbackURL: window.location.origin + next });
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-page px-4 py-10">
      <div className="w-full max-w-[400px]">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-[44px] w-[44px] items-center justify-center rounded-[12px] bg-sidebar font-display text-[18px] font-extrabold text-white">P</div>
          <div>
            <div className="font-display text-[20px] font-bold">Pilote</div>
            <div className="text-[12.5px] text-muted">Tableau de bord du réseau</div>
          </div>
        </div>
        <div className="rounded-card border border-line bg-white px-6 py-7">
          <div className="mb-5 font-display text-[18px] font-bold">{mode === "signin" ? "Connexion" : "Créer mon compte"}</div>
          {apple && (
            <>
              <button
                type="button"
                onClick={() => void signInApple()}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-black py-[11px] text-[14px] font-semibold text-white"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M16.37 1.43c0 1.14-.42 2.2-1.25 3.1-.99 1.07-2.19 1.69-3.49 1.59a3.5 3.5 0 0 1-.03-.43c0-1.09.47-2.26 1.31-3.19.42-.47.95-.86 1.6-1.17.64-.3 1.25-.47 1.83-.5.02.2.03.4.03.6zM21 17.19c-.35.8-.76 1.54-1.24 2.22-.65.93-1.18 1.57-1.59 1.93-.64.59-1.33.89-2.06.9-.53 0-1.16-.15-1.9-.45-.74-.3-1.42-.45-2.04-.45-.65 0-1.35.15-2.1.45-.75.3-1.35.46-1.81.48-.7.03-1.4-.28-2.1-.93-.44-.38-1-1.04-1.66-1.98-.71-1-1.3-2.16-1.76-3.48-.49-1.43-.74-2.81-.74-4.15 0-1.54.33-2.86 1-3.98a5.86 5.86 0 0 1 2.09-2.11 5.63 5.63 0 0 1 2.83-.8c.56 0 1.29.17 2.19.51.9.34 1.48.51 1.73.51.19 0 .83-.2 1.92-.6 1.03-.37 1.9-.53 2.62-.47 1.93.16 3.39.92 4.35 2.29-1.73 1.05-2.59 2.52-2.57 4.4.02 1.47.55 2.69 1.59 3.66.47.45 1 .79 1.58 1.03-.13.37-.26.72-.4 1.06z" />
                </svg>
                {mode === "signin" ? "Se connecter avec Apple" : "Continuer avec Apple"}
              </button>
              <div className="my-5 flex items-center gap-3 text-[11.5px] text-faint">
                <div className="h-px flex-1 bg-line" /> ou <div className="h-px flex-1 bg-line" />
              </div>
            </>
          )}
          <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-3">
            {mode === "signup" && (
              <label className="block">
                <span className="mb-1 block text-[12px] font-semibold text-muted">Nom complet</span>
                <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required />
              </label>
            )}
            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold text-muted">E-mail</span>
              <input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
            </label>
            <label className="block">
              <span className="mb-1 block text-[12px] font-semibold text-muted">Mot de passe</span>
              <input
                className={inputClass}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === "signin" ? "current-password" : "new-password"}
                minLength={mode === "signup" ? 10 : undefined}
                required
              />
              {mode === "signup" && <span className="mt-1 block text-[11px] text-faint">10 caractères minimum.</span>}
            </label>
            {mode === "signup" && !params.get("next")?.startsWith("/invitation/") && (
              <div className="rounded-[10px] bg-page px-3 py-2 text-[12px] text-muted">
                L'accès est soumis à validation : votre compte sera activé par un administrateur (ou directement si
                vous avez un lien d'invitation).
              </div>
            )}
            {error && <div className="rounded-[10px] bg-danger-bg px-3 py-2 text-[12.5px] font-semibold text-danger">{error}</div>}
            <button type="submit" disabled={busy} className="mt-1 rounded-full bg-accent py-[11px] text-[14px] font-bold text-white disabled:opacity-50">
              {busy ? "…" : mode === "signin" ? "Se connecter" : "Créer mon compte"}
            </button>
          </form>
        </div>
        <div className="mt-4 text-center text-[12.5px] text-muted">
          {mode === "signin" ? (
            <>
              Invité·e à rejoindre un réseau ?{" "}
              <button type="button" className="font-semibold text-accent" onClick={() => setMode("signup")}>
                Créer un compte
              </button>
            </>
          ) : (
            <>
              Déjà un compte ?{" "}
              <button type="button" className="font-semibold text-accent" onClick={() => setMode("signin")}>
                Se connecter
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
